import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { ErrorMessage } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ErrorMessage";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import patchUser from "@faire/web-api--source/endpoints/www/api/user/patch";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useStrictLocalization } from "@faire/web/common/localization";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Input } from "@faire/web/slate/Input";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useState } from "react";

type CreatePasswordResult = {
  nextView: View;
};

interface IProps {
  loggedInUser: IUser;
  onComplete: (result: CreatePasswordResult) => void;
  onLogin: (user: IUser) => void;
}

const INPUT_IDENTIFIER = "password";

export const CreatePasswordView: FC<IProps> = ({
  loggedInUser,
  onComplete,
  onLogin,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<LoginError>();
  const { trackFieldValue } = useSignInEventTracker("CREATE_PASSWORD");

  const onContinue = async () => {
    trackEvent("/login-modal/set-password");
    setError(undefined);

    try {
      await patchUser({
        token: loggedInUser.token,
        password,
      });

      trackEvent("/login-modal/logged-in-after-setting-password");
      onLogin(loggedInUser);
      onComplete({ nextView: View.LOADING });
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    }
  };

  return (
    <LoginWrapper>
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="ARjKpf"
          defaultMessage="Create a password"
          description={{ text: "Let users set a password for a new account" }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          id="xwVYsP"
          defaultMessage="This will make it easy for you to log into your account in the future"
        />
      </Typography>
      <Spacer height="3x" />
      <Input
        autoFocus
        autoComplete="new-password"
        placeholder={strictLocalize({
          id: "CvZkU7",
          defaultMessage: "Create Password",
          description: {
            text: "Placeholder text for user to input a new password",
          },
        })}
        name={INPUT_IDENTIFIER}
        type={INPUT_IDENTIFIER}
        role={INPUT_IDENTIFIER}
        value={password}
        onChange={(event) => {
          setPassword(event.currentTarget.value);
          trackFieldValue(INPUT_IDENTIFIER, "");
        }}
        onKeyDown={handleKeyDown(onContinue)}
      />
      <Spacer height="4x" />
      <ContinueToNextViewButton onClick={onContinue} />
      <ErrorMessage error={error} />
    </LoginWrapper>
  );
};
